import React, { useState, useCallback } from 'react';
import { convertDigitToProperFormat } from 'utils/number';
import QuestionInfo from '../QuestionInfo';
import Chevron from 'components/Chevron';
import {
  OptionalTitle,
  TitleWrapper,
  QuestionTitle,
  ChevronButton,
  ChevronWrapper,
  ChevronText,
  IndexBadge,
  GeminiIconWrapper,
  GenerateContentViaAiLabel,
  GeneratingDots,
  GenerateContentViaAIButton,
} from './styles';
import Button from '../../Button/Button';
import LocalIcon from 'ui/LocalIcon';
import { green, stringToColour } from '../../../utils/style/colors';
import Badge from '../../Badge/Badge';
import { withTitle } from '../../withTitle';
import { ITags, ITagView } from 'store/providers/providerTypes';
import {
  IAnswerView,
  IQuestionField,
  IQuestionsView,
  ISurveyView,
} from 'store/survey/surveyTypes';
import i18n from '../../../i18n';
import handleQuestionFromChatBot, {
  handleGenerateImageFromChatBot,
} from 'api/chatbot-api/AskQuestion';
import { DEFAULT_AI_GPT_MODEL } from 'api/chatbot-api/aiModels';
import notify from 'components/Notify';
import { buildNewsComplementaryContext } from './buildNewsComplementaryContext';
import Icon from 'components/Icon';

interface ITitleInputProps {
  hint: string;
  title: string;
  tags: ITagView[];
  allTags?: ITags['data'];
  badge?: string;
  defaultOpenHint?: boolean;
  fields?: IQuestionField[];
  required: boolean;
  dataSourceName?: string;
  onShowDataSourceModal?: (dataSourceName: string) => void;
  indexNumber?: number;
  survey?: ISurveyView;
  answers?: { [key: number]: IAnswerView };
  questions?: { [questionId: number]: IQuestionsView };
  getNewsComplementaryContext?: () => string;
  onNewsGenerated?: (content: string) => void;
  onImageGenerated?: (dataUri: string) => void;
  aiGptModel?: string;
}

const TitleInput: React.FC<ITitleInputProps> = ({
  hint,
  title,
  fields,
  required,
  dataSourceName,
  onShowDataSourceModal,
  tags,
  allTags,
  badge,
  defaultOpenHint,
  indexNumber,
  survey,
  answers,
  questions,
  getNewsComplementaryContext,
  onNewsGenerated,
  onImageGenerated,
  aiGptModel,
}) => {
  const [isActive, setActive] = useState(
    defaultOpenHint === undefined
      ? !!survey?.fields?.find(
          (f) => f.name === 'conf_openHints' && f.value !== 'false'
        )
      : defaultOpenHint
  );
  const handleSetActive = () => {
    setActive((isActive) => !isActive);
  };
  const handleSetActiveCallback = useCallback(handleSetActive, []);
  const finalField =
    fields &&
    fields?.filter((k) => !k.name.startsWith('conf_') && k.name !== '');
  const showQuestionInfo = !!hint || (!!finalField && finalField.length > 0);
  const hideTags = !!survey?.fields?.find(
    (f) => f.name === 'conf_hideQuestionTags'
  );
  const notShowIndex =
    survey?.surveyType === 'Exam' ||
    survey?.randomizeOrder ||
    survey?.fields
      ?.find((item) => {
        return item.name === 'conf_nonNumbering';
      })
      ?.value.toLowerCase() === 'true';

  const isNewsComplementoryQuestion =
    fields
      ?.find((f) => f.name === 'conf_newsComplementoryQuestionForNews')
      ?.value.toLowerCase() === 'true';

  const isNewsQuestionToComplete =
    fields
      ?.find((f) => f.name === 'conf_newsQuestionToComplete')
      ?.value.toLowerCase() === 'true';

  const [generatingNews, setGeneratingNews] = useState<boolean>(false);
  const [generatingImage, setGeneratingImage] = useState<boolean>(false);

  const enableSingleQuestionAICompletion = !(
    survey?.fields
      ?.find((f) => f.name === 'conf_AIFillOutOtherFieldsBeforeSubmit')
      ?.value.toLowerCase() === 'true'
  );

  const enableImageGeneration =
    fields
      ?.find((f) => f.name === 'conf_enableImageGeneration')
      ?.value.toLowerCase() === 'true';

  const handleGenerateImageViaAI = async () => {
    const systemPrompt = fields?.find(
      (f) => f.name === 'conf_systemPrompt'
    )?.value;

    const complementaryContext =
      getNewsComplementaryContext?.() ??
      buildNewsComplementaryContext(questions, answers, allTags);

    if (!complementaryContext) {
      notify(
        i18n.t('newsComplementaryAnswersRequired', { ns: 'survey' }) as string,
        'error'
      );
      return;
    }

    const instruction = [
      {
        role: 'developer',
        content: [
          {
            type: 'input_text',
            text:
              systemPrompt ??
              'You are a helpful, professional photographer that delivers spectacular, eye-catching, and realistic photos based on the user information. Do not respond with text — only generate an image.',
          },
        ],
      },
      {
        role: 'user',
        content: [{ type: 'input_text', text: complementaryContext }],
      },
    ];

    setGeneratingImage(true);
    try {
      const { text: result } = await handleGenerateImageFromChatBot(
        instruction,
        aiGptModel || DEFAULT_AI_GPT_MODEL
      );
      if (result) {
        onImageGenerated?.(result);
        notify(
          i18n.t('imageGeneratedSuccessfully', { ns: 'survey' }) as string
        );
      } else {
        notify(
          i18n.t('imageGenerationFailed', { ns: 'survey' }) as string,
          'error'
        );
      }
    } catch {
      notify(
        i18n.t('imageGenerationFailed', { ns: 'survey' }) as string,
        'error'
      );
    } finally {
      setGeneratingImage(false);
    }
  };

  const handleGenerateNews = async () => {
    const systemPrompt = fields?.find(
      (f) => f.name === 'conf_systemPrompt'
    )?.value;

    const complementaryContext =
      getNewsComplementaryContext?.() ??
      buildNewsComplementaryContext(questions, answers, allTags);

    if (!complementaryContext) {
      notify(
        i18n.t('newsComplementaryAnswersRequired', { ns: 'survey' }) as string,
        'error'
      );
      return;
    }

    const instruction = [
      {
        role: 'developer',
        content: [
          {
            type: 'input_text',
            text:
              systemPrompt ??
              'You are a helpful, professional reporter assistant that can extract the news from the content and return it in a text format. Your response should precise and follow all news regarding it. Note: Do not get back with question and try to generate whatever the user is asking for.',
          },
        ],
      },
      {
        role: 'user',
        content: [{ type: 'input_text', text: complementaryContext }],
      },
    ];

    setGeneratingNews(true);
    try {
      const { text: result } = await handleQuestionFromChatBot(
        instruction,
        true,
        aiGptModel || DEFAULT_AI_GPT_MODEL
      );
      if (result) {
        onNewsGenerated?.(result);
        notify(i18n.t('newsGeneratedSuccessfully', { ns: 'survey' }) as string);
      }
    } catch {
      notify(
        i18n.t('newsGenerationFailed', { ns: 'survey' }) as string,
        'error'
      );
    } finally {
      setGeneratingNews(false);
    }
  };

  return (
    <div>
      <TitleWrapper>
        <QuestionTitle>
          <span
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              flexWrap: 'wrap',
            }}
          >
            {!notShowIndex && (
              <IndexBadge>
                {convertDigitToProperFormat(indexNumber?.toString() || '')}
              </IndexBadge>
            )}
            <span style={{ fontWeight: '600' }}>{title}</span>
            {required || survey?.surveyType === 'Exam' ? null : (
              <OptionalTitle>
                {i18n.t('optional', { ns: 'survey' }) as string}
              </OptionalTitle>
            )}
            {isNewsComplementoryQuestion &&
              withTitle(
                <Icon color={green} type="info" />,
                i18n.t('conf_newsComplementoryQuestionForNews', {
                  ns: 'survey',
                }) as string
              )}
          </span>
        </QuestionTitle>
        <div
          className={'hide-on-mobile'}
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            maxWidth: '30%',
            gap: '3px',
          }}
        >
          {!hideTags &&
            allTags &&
            tags &&
            tags
              .filter((tag: ITagView) => !tag.text.startsWith('conf_'))
              .map((it) => {
                const text = allTags[it.id]?.text;
                const parent = allTags[it.id]?.parentId;
                return withTitle(
                  <Badge
                    key={it.id}
                    color={stringToColour(
                      text ?? '',
                      1,
                      false,
                      parent?.toString()
                    )}
                    style={{ margin: '0px 2px' }}
                  >
                    {text}
                  </Badge>,
                  parent ? allTags[parent]?.text : ''
                );
              })}
        </div>
        <span>
          {dataSourceName && (
            <Button
              style={{ padding: '2px 4px' }}
              color="green"
              onClick={() => onShowDataSourceModal?.(dataSourceName)}
            >
              {i18n.t('selectAValue', { ns: 'survey' }) as string}
            </Button>
          )}
          {badge && (
            <Badge color="green" style={{ marginInlineEnd: '1em' }}>
              {badge}
            </Badge>
          )}
          {showQuestionInfo && (
            <ChevronButton onClick={handleSetActiveCallback}>
              <ChevronText>
                {i18n.t('description', { ns: 'survey' }) as string}
              </ChevronText>
              <ChevronWrapper>
                <Chevron isOpen={isActive} fill={green} />
              </ChevronWrapper>
            </ChevronButton>
          )}
          {enableImageGeneration && onImageGenerated && (
            <GenerateContentViaAIButton
              generating={generatingImage}
              disabled={generatingImage}
              onClick={handleGenerateImageViaAI}
              type="button"
            >
              <GeminiIconWrapper generating={generatingImage}>
                <LocalIcon
                  iconName="gemini-logo.svg"
                  type="xs"
                  color="no-color"
                  style={{ margin: 0, height: '2rem' }}
                />
              </GeminiIconWrapper>
              <GenerateContentViaAiLabel>
                {generatingImage
                  ? (i18n.t('generatingImageViaAI', {
                      ns: 'survey',
                    }) as string)
                  : (i18n.t('generateImageViaAI', {
                      ns: 'survey',
                    }) as string)}
              </GenerateContentViaAiLabel>
              {generatingImage && (
                <GeneratingDots aria-hidden="true">
                  <span />
                  <span />
                  <span />
                </GeneratingDots>
              )}
            </GenerateContentViaAIButton>
          )}
          {isNewsQuestionToComplete && enableSingleQuestionAICompletion && (
            <GenerateContentViaAIButton
              generating={generatingNews}
              disabled={generatingNews}
              onClick={handleGenerateNews}
              type="button"
            >
              <GeminiIconWrapper generating={generatingNews}>
                <LocalIcon
                  iconName="gemini-logo.svg"
                  type="xs"
                  color="no-color"
                  style={{ margin: 0, height: '2rem' }}
                />
              </GeminiIconWrapper>
              <GenerateContentViaAiLabel>
                {generatingNews
                  ? (i18n.t('generatingContentViaAI', {
                      ns: 'survey',
                    }) as string)
                  : (i18n.t('generateContentViaAI', {
                      ns: 'survey',
                    }) as string)}
              </GenerateContentViaAiLabel>
              {generatingNews && (
                <GeneratingDots aria-hidden="true">
                  <span />
                  <span />
                  <span />
                </GeneratingDots>
              )}
            </GenerateContentViaAIButton>
          )}
        </span>
      </TitleWrapper>
      {showQuestionInfo && (
        <QuestionInfo
          show={isActive}
          fields={
            fields?.filter(
              (el) => !el.name.startsWith('conf_') && el.name !== ''
            ) || []
          }
          hint={hint}
        />
      )}
    </div>
  );
};

export default TitleInput;
